/**
 * Payroll Import — template download, upload, batch history, row review, post.
 * Authority: HR_PAYROLL_PROCESS | HR_PAYROLL_MANAGE
 *
 * Unlike Inventory's import, there's no separate maker-checker approval on the batch itself —
 * the period already goes through PAYROLL approval once calculated, so upload -> validate -> post
 * is direct (two calls, so HR can review row errors before committing).
 * Posting also auto-triggers calculation; if that can't run yet, the period is left at
 * IMPORTED for a manual /calculate call from the period detail page.
 *
 * Endpoints:
 *   GET  /import/template
 *   POST /periods/{periodId}/import   multipart file
 *   POST /import/{batchId}/post
 *   GET  /periods/{periodId}/import?page&size
 *   GET  /import/{batchId}/rows?onlyErrors&page&size
 */
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle, Download, FileSpreadsheet, Upload, X,
} from "lucide-react";
import { payrollApi, downloadBlob } from "../api";
import type { PayrollImportRow } from "../types";
import { useAsyncData, useMutation, usePagination } from "../hooks";
import {
  BackBtn, Btn, Card, CardHeader, EmptyState, PageHeader,
  PayrollError, PayrollLoading, Pagination,
  Table, TBody, TD, TH, THead, TR, fmtDateTime,
} from "../components/PayrollUI";

const BATCH_STATUS_CLS: Record<string, string> = {
  VALIDATED: "bg-blue-100 text-blue-700",
  PARTIALLY_VALID: "bg-amber-100 text-amber-700",
  FAILED: "bg-red-100 text-red-600",
  POSTED: "bg-emerald-100 text-emerald-700",
};

function RowsModal({ batchId, batchNo, onClose }: { batchId: number; batchNo: string; onClose: () => void }) {
  const [onlyErrors, setOnlyErrors] = useState(false);
  const pagination = usePagination(0, 25);
  const { data, loading, error, reload } = useAsyncData(
    () => payrollApi.listImportRows(batchId, { onlyErrors, page: pagination.page, size: pagination.size }),
    [batchId, onlyErrors, pagination.page, pagination.size],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Batch {batchNo} — Rows</h2>
            <label className="mt-1 flex items-center gap-2 text-xs font-semibold text-slate-500">
              <input type="checkbox" checked={onlyErrors} onChange={(e) => { setOnlyErrors(e.target.checked); pagination.reset(); }} />
              Show only error rows
            </label>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        {loading && <PayrollLoading label="Loading rows…" />}
        {error && <PayrollError message={error} onRetry={reload} />}

        {!loading && data && (
          <>
            <Table>
              <THead>
                <TH>Row</TH><TH>Employee No.</TH><TH>Basic Pay</TH><TH>House</TH>
                <TH>Transport</TH><TH>Other</TH><TH>Status</TH>
              </THead>
              <TBody>
                {data.content.length === 0 ? (
                  <tr><td colSpan={7}><EmptyState icon={FileSpreadsheet} title="No rows" /></td></tr>
                ) : data.content.map((r: PayrollImportRow) => (
                  <TR key={r.id} className={r.validRow ? "" : "bg-red-50/60"}>
                    <TD muted>{r.rowNumber}</TD>
                    <TD>{r.employeeNumber ?? "—"}</TD>
                    <TD muted>{r.basicPay ?? "—"}</TD>
                    <TD muted>{r.houseAllowance ?? "—"}</TD>
                    <TD muted>{r.transportAllowance ?? "—"}</TD>
                    <TD muted>{r.otherAllowance ?? "—"}</TD>
                    <TD>
                      {r.validRow
                        ? <span className="text-xs font-semibold text-emerald-600">Valid</span>
                        : <span className="text-xs font-semibold text-red-600">{r.errorMessage ?? "Invalid"}</span>}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="mt-3">
              <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function PayrollImportPage() {
  const { id } = useParams();
  const periodId = Number(id);
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [dlLoading, setDlLoading] = useState(false);
  const [rowsModal, setRowsModal] = useState<{ id: number; batchNo: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const batches = useAsyncData(() => payrollApi.listImportBatches(periodId, 0, 20), [periodId]);

  const upload = useMutation((f: File) => payrollApi.uploadImport(periodId, f));
  const post = useMutation((batchId: number) => payrollApi.postImportBatch(batchId));

  async function downloadTemplate() {
    setDlLoading(true);
    try {
      const blob = await payrollApi.downloadImportTemplate();
      downloadBlob(blob, "payroll_basic_pay_template.xlsx");
    } finally {
      setDlLoading(false);
    }
  }

  async function doUpload() {
    if (!file) return;
    await upload.mutate(file);
    setFile(null);
    batches.reload();
  }

  async function doPost(batchId: number) {
    await post.mutate(batchId);
    batches.reload();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Finance · Import"
        title="Import Basic Pay"
        actions={<BackBtn to={`/payroll/periods/${periodId}`} label="Period" />}
      />

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { step: "1", title: "Download Template", desc: "Pre-filled with active employees for this period." },
          { step: "2", title: "Fill in Figures", desc: "Basic pay is required; allowances default to 0. Don't rename columns." },
          { step: "3", title: "Upload & Post", desc: "Review any error rows, then post to commit and auto-calculate." },
        ].map((s) => (
          <div key={s.step} className="flex gap-4 rounded-2xl border bg-white p-5 shadow-sm">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">{s.step}</div>
            <div>
              <p className="font-semibold text-slate-800">{s.title}</p>
              <p className="mt-0.5 text-xs text-slate-400">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <Card>
        <CardHeader title="Step 1 — Download Template" subtitle="EMPLOYEE_NUMBER (required, key), EMPLOYEE_NAME (reference only), BASIC_PAY (required). Recurring allowances are configured once per employee under Employee Benefits, not here." />
        <div className="flex items-center gap-4 p-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
            <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-800">payroll_basic_pay_template.xlsx</p>
          </div>
          <Btn variant="emerald" icon={Download} loading={dlLoading} loadingText="Downloading…" onClick={downloadTemplate}>Download</Btn>
        </div>
      </Card>

      <Card>
        <CardHeader title="Step 3 — Upload Completed File" />
        <div className="space-y-4 p-5">
          <div
            className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-10 transition ${file ? "border-indigo-400 bg-indigo-50/40" : "border-slate-200 bg-slate-50 hover:border-indigo-300"}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <Upload className="h-8 w-8 text-slate-300" />
            {file ? (
              <div className="text-center">
                <p className="font-semibold text-indigo-700">{file.name}</p>
                <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                <button type="button" className="mt-2 text-xs text-red-500 hover:underline" onClick={() => setFile(null)}>Remove</button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-700">
                  Drop your file here or{" "}
                  <button type="button" className="text-indigo-600 underline" onClick={() => fileRef.current?.click()}>browse</button>
                </p>
                <p className="mt-1 text-xs text-slate-400">Supported: .xlsx</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); }} />
          </div>
          <div className="flex justify-end">
            <Btn variant="primary" icon={Upload} loading={upload.loading} loadingText="Uploading…" disabled={!file} onClick={doUpload}>Upload</Btn>
          </div>
          {upload.error && (
            <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
              <p className="text-sm text-red-700">{upload.error}</p>
            </div>
          )}
        </div>
      </Card>

      <Card noPad>
        <CardHeader title="Import Batches" />
        {batches.loading ? <PayrollLoading /> : batches.error ? <div className="p-5"><PayrollError message={batches.error} onRetry={batches.reload} /></div> : (
          <Table>
            <THead>
              <TH>Batch</TH><TH>File</TH><TH right>Total</TH><TH right>Valid</TH><TH right>Invalid</TH>
              <TH>Status</TH><TH>Uploaded By</TH><TH>Uploaded</TH><TH right>Actions</TH>
            </THead>
            <TBody>
              {!(batches.data?.content.length) ? (
                <tr><td colSpan={9}><EmptyState icon={FileSpreadsheet} title="No import batches yet" description="Upload a completed template above to get started." /></td></tr>
              ) : batches.data!.content.map((b) => (
                <TR key={b.id}>
                  <TD className="font-mono text-xs">{b.batchNo ?? b.id}</TD>
                  <TD muted>{b.originalFileName ?? "—"}</TD>
                  <TD right muted>{b.totalRows ?? "—"}</TD>
                  <TD right muted>{b.validRows ?? "—"}</TD>
                  <TD right muted>{b.invalidRows ?? "—"}</TD>
                  <TD><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${BATCH_STATUS_CLS[b.status] ?? "bg-slate-100 text-slate-600"}`}>{String(b.status).replace(/_/g, " ")}</span></TD>
                  <TD muted>{b.uploadedByUsername ?? "—"}</TD>
                  <TD muted>{fmtDateTime(b.uploadedAt)}</TD>
                  <TD right>
                    <div className="flex justify-end gap-1.5">
                      <Btn size="sm" onClick={() => setRowsModal({ id: b.id, batchNo: b.batchNo ?? String(b.id) })}>Rows</Btn>
                      {(b.status === "VALIDATED" || b.status === "PARTIALLY_VALID") && (
                        <Btn size="sm" variant="primary" loading={post.loading} loadingText="Posting…" onClick={() => doPost(b.id)}>Post</Btn>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
        {post.error && <p className="px-5 pb-4 text-sm text-red-600">{post.error}</p>}
      </Card>

      <div className="flex justify-end">
        <Btn variant="secondary" onClick={() => navigate(`/payroll/periods/${periodId}`)}>Back to Period</Btn>
      </div>

      {rowsModal && (
        <RowsModal batchId={rowsModal.id} batchNo={rowsModal.batchNo} onClose={() => setRowsModal(null)} />
      )}
    </div>
  );
}
